import React from "react";
import PropTypes from "prop-types";
import { env } from "utils/env";
import omitDeep from "omit-deep-lodash";
import { RetryLink } from "@apollo/client/link/retry";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client";
import { ApolloProvider, split } from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { createUploadLink } from "apollo-upload-client";
import { useSelector } from "react-redux";
import userManager from "utils/userManager";
import i18next from "i18next";
import { emptyObject } from "../constants/variousTypes";
import { partnerTypeIdVar } from "variables";

const cache = new InMemoryCache({
  typePolicies: {
    PartnerType: {
      keyFields: ["partnerTypeId"]
    },
    PartnerBalanceSheetDetailWithAccountConfig: {
      keyFields: ["balanceSheetDetailId"]
    },
    PersonWorkplace: {
      keyFields: ["personWorkplaceId"]
    },
    PartnerBalanceSheetIndicator: {
      keyFields: ["balanceSheetIndicatorId"]
    },
    PartnerExtraInformationModel: {
      keyFields: ["partnerId"]
    },
    PartnerHierarchy: {
      keyFields: ["partnerHierarchyId"]
    },
    PartnerHierarchyDetail: {
      keyFields: false
    },
    PartnerHierarchyDetailTreeNode: {
      keyFields: false
    },
    PartnerMessage: {
      keyFields: ["partnerId"]
    }
  }
});

cache.restore();

let wsLink = null;
const getWsLink = () => {
  if (!wsLink) {
    let activeSocket, currentAccessToken;
    const wsClient = createClient({
      url: `${env.REACT_APP_GQL_WS_PROTOCOL}://${env.REACT_APP_GQL}/graphql`,
      keepAlive: 10000, // ping server every 10 seconds
      connectionParams: async () => {
        const { access_token } = (await userManager.getUser()) ?? emptyObject;
        currentAccessToken = access_token;

        return {
          authorization: access_token ? `Bearer ${access_token}` : "",
          "accept-language": i18next?.language
        };
      },
      on: {
        connected: socket => (activeSocket = socket),
        ping: async received => {
          if (!received) {
            const { access_token } = (await userManager.getUser()) ?? emptyObject;
            if (activeSocket.readyState === WebSocket.OPEN && currentAccessToken !== access_token) {
              setTimeout(() => {
                activeSocket.close(1000, "Access token silent renew");
              }, 1000); // wait 1 second for the pong and then close the connection
            }
          }
        },
        closed: event => {
          console.log(`GraphQL WebSocket closed!: ${event.code} Reason: ${event.reason}`);
        },
        error: onError(({ graphQLErrors, networkError }) => {
          if (graphQLErrors)
            graphQLErrors.map(({ message, locations, path }) =>
              console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
            );
          if (networkError) console.log(`[Network error]: ${networkError}`);
        })
      }
    });
    wsLink = new GraphQLWsLink(wsClient);
  }
  return wsLink;
};

const httpLink = () =>
  createUploadLink({
    uri: `${env.REACT_APP_GQL_HTTP_PROTOCOL}://${env.REACT_APP_GQL}/graphql`,
    onError: onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors)
        graphQLErrors.map(({ message, locations, path }) =>
          console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
        );
      if (networkError) console.log(`[Network error]: ${networkError}`);
    })
  });

const link = split(
  ({ query }) => {
    const { kind, operation } = getMainDefinition(query);
    return kind === "OperationDefinition" && operation === "subscription";
  },
  getWsLink(),
  httpLink()
);

const omitTypenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = omitDeep(operation.variables, ["__typename"]);
  }
  return forward(operation);
});

const retryLink = new RetryLink({ delay: { initial: 200, max: 2000, jitter: true }, attempts: { max: 3 } });

const authLink = setContext(async (_, { headers, serviceName }) => {
  const user = await userManager.getUser();
  const partnerTypeId = partnerTypeIdVar();

  return {
    headers: {
      ...headers,
      authorization: user?.access_token ? `Bearer ${user?.access_token}` : "",
      service: serviceName,
      "accept-language": i18next?.language,
      "masterpartner-partnertypeid": `${partnerTypeId}`
    }
  };
});

const myAppLink = ApolloLink.from([omitTypenameLink, retryLink, authLink.concat(link)]);

const client = new ApolloClient({ link: myAppLink, cache, resolvers: {} });

export default function AuthApolloProvider({ children }) {
  const loading = useSelector(state => Boolean(state?.oidc?.isLoading));

  if (loading) {
    return <>auth loading</>;
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}

AuthApolloProvider.propTypes = {
  children: PropTypes.element.isRequired
};

export { cache };

let apolloClient;
export const getApolloClient = () => {
  if (!apolloClient) {
    apolloClient = new ApolloClient({
      link: myAppLink,
      cache
    });
  }
  return apolloClient;
};
